// Deep-link helpers used by the customer info panel. Each function returns
// `undefined` when the input is missing or the destination URL has not been
// sourced yet — callers should treat `undefined` as "do not render a link"
// and fall back to plain text.

// VITE_TW_BASE_URL may or may not include a trailing slash depending on the
// environment file, so strip any so the URLs we produce are well-formed.
const baseUrl: string | undefined = (
  import.meta.env.VITE_TW_BASE_URL as string | undefined
)?.replace(/\/+$/, "")

const buildSearchUrl = (
  path: string,
  params: Record<string, string>
): string | undefined => {
  if (!baseUrl) return undefined
  const search = new URLSearchParams(params).toString()
  return `${baseUrl}/admin/${path}?${search}`
}

export function customersByEmailUrl(
  email: string | null | undefined
): string | undefined {
  if (!email) return undefined
  return buildSearchUrl("customers", {
    utf8: "✓",
    "q[email_eq]": email,
    commit: "Filter",
    order: "id_desc",
  })
}

export function usersByUsernameUrl(
  username: string | null | undefined
): string | undefined {
  if (!username) return undefined
  return buildSearchUrl("users", {
    utf8: "✓",
    "q[username_eq]": username,
    commit: "Filter",
    order: "id_desc",
  })
}

export function accountsByAccountNumberUrl(
  accountNumber: string | null | undefined
): string | undefined {
  if (!accountNumber) return undefined
  return buildSearchUrl("accounts", {
    order: "id_desc",
    "q[account_number_eq]": accountNumber,
    scope: "all",
    utf8: "✓",
  })
}

// ATP-14034: Salesforce Account page link, keyed by the customer's
// `ext-crm-id` (deserialized onto `Customer.extCrmId`). The trade desk
// shows this on the Customer Details view's Name field; when the field
// is missing or empty the caller falls back to plain text.
export function salesforceCustomerProfileUrl(
  extCrmId: string | null | undefined
): string | undefined {
  if (!extCrmId) return undefined
  return `https://tastyworks.lightning.force.com/lightning/r/Account/${extCrmId}/view`
}

// Tableau's filter key is `ID Search`, and the dashboard's link scheme spells
// the space as %20, so the key is written pre-encoded — URLSearchParams would
// emit `+` instead.
export function tableauCustomerRevenueUrl(
  externalCustomerId: string | null | undefined
): string | undefined {
  if (!externalCustomerId) return undefined
  return `https://prod-useast-b.online.tableau.com/#/site/tastytrade/views/CustomerRevenueLookup/CustomerRevenueLookup?ID%20Search=${encodeURIComponent(externalCustomerId)}`
}

export function accountTradeStatusUrl(
  accountNumber: string | null | undefined
): string | undefined {
  if (!accountNumber) return undefined
  return buildSearchUrl("account_trade_statuses", {
    utf8: "✓",
    "q[account_number_eq]": accountNumber,
  })
}

export function pendingCashEntriesByAccountNumberUrl(
  accountNumber: string | null | undefined
): string | undefined {
  if (!accountNumber) return undefined
  return buildSearchUrl("pending_cash_entries", {
    utf8: "✓",
    "q[account_number_eq]": accountNumber,
  })
}

export function userSessionsUrl(
  email: string | null | undefined
): string | undefined {
  if (!email) return undefined
  return buildSearchUrl("sessions", {
    utf8: "✓",
    "q[user_email_eq]": email,
    commit: "Filter",
    order: "id_desc",
  })
}

export function accountDocumentsUrl(
  accountNumber: string | null | undefined
): string | undefined {
  if (!accountNumber) return undefined
  return buildSearchUrl("account_documents", {
    account_number: accountNumber,
  })
}
